import Markdown from 'markdown-to-jsx';
import React, { useRef, useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  HStack,
  Icon,
  Input,
  NewButton,
  P,
  cn,
  legacyTextStyles,
  useToast,
} from '@eluve/components';
import { Snippet, SnippetModal } from '@eluve/eluve-editor';
import { useSnippets } from '@eluve/frontend-appointment-hooks';
import { useDialog } from '@eluve/utility-hooks';
import { getVendorDataMapper } from '@eluve/vendor';

export const TenantUserSnippetsList = () => {
  const { snippets, loading, addSnippet, updateSnippet, deleteSnippet } =
    useSnippets();
  const toast = useToast();

  const { isDialogOpen, setIsDialogOpen } = useDialog();
  const [editSnippetId, setEditSnippetId] = useState<string>();
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resetModal = () => {
    setIsDialogOpen(false);
    setEditSnippetId(undefined);
  };

  const onAddNewClick = () => {
    setIsDialogOpen(true);
    setEditSnippetId(undefined);
  };

  const saveSnippet = async (title: string, content: string) => {
    resetModal();
    if (editSnippetId) {
      await updateSnippet(editSnippetId, title, content);
    } else {
      await addSnippet(title, content);
    }
  };

  const editSnippet = (snippetId: string) => {
    setIsDialogOpen(true);
    const snippet = snippets.find((s) => s.id === snippetId);
    if (!snippetId || !snippet) {
      return;
    }
    setEditSnippetId(snippetId);
  };

  const handleExport = () => {
    const snippetJson = JSON.stringify(
      snippets.map((s) => ({ title: s.title, content: s.content })),
      null,
      2,
    );
    const blob = new Blob([snippetJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'eluve-snippets.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setIsImporting(true);
      const text = await file.text();
      const jsonData = JSON.parse(text);
      let importedSnippets: Snippet[] = [];

      if (Array.isArray(jsonData)) {
        importedSnippets = jsonData;
      } else if (jsonData?.snippets && Array.isArray(jsonData?.snippets)) {
        importedSnippets = jsonData.snippets;
      } else if (typeof jsonData === 'object' && jsonData !== null) {
        const vendorProvider = getVendorDataMapper('JANE');
        if (vendorProvider.convertPhrasesToSnippets) {
          importedSnippets = vendorProvider.convertPhrasesToSnippets(jsonData);
        }
      } else {
        throw new Error('Unknown file format');
      }

      if (importedSnippets?.length > 0) {
        await Promise.all(
          importedSnippets.map((snippet) =>
            addSnippet(snippet.title, snippet.content),
          ),
        );
        toast.success(
          `Imported ${importedSnippets.length} snippets successfully`,
        );
      }
    } catch {
      toast.error('Error parsing snippets file');
    } finally {
      e.target.value = '';
      setIsImporting(false);
    }
  };

  return (
    <div className="w-full">
      <HStack inline justify="start" className="mb-4">
        <div
          className={legacyTextStyles.body({
            size: 's',
            weight: 'medium',
            color: 'primary',
          })}
        >
          Create reusable text to streamline your documentation. To insert a
          snippet, type{' '}
          <code className="rounded-md border border-border-secondary bg-bg-secondary px-1 py-0.5 font-mono text-sm text-text-tertiary shadow">
            /
          </code>{' '}
          in the editor and select your shortcut.
        </div>
        <HStack justify="end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <NewButton
                loading={isImporting}
                type="outlineSubtle"
                icon={{ name: 'EllipsisVertical' }}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={handleExport}>
                <Icon name="Upload" size="sm" />
                <div>Export</div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleImport}>
                <Icon name="Import" size="sm" />
                <div>Import</div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <NewButton text="Create New" onClick={onAddNewClick} />
        </HStack>
      </HStack>

      <Input
        type="file"
        accept=".json"
        ref={fileInputRef}
        onChange={onFileSelected}
        className="hidden"
      />

      <div>
        {loading ? (
          <HStack>Loading ...</HStack>
        ) : (
          snippets.map((snippet) => (
            <HStack
              justify="between"
              key={String(snippet.id)}
              className={cn(
                'mb-4 rounded-md border border-border-primary p-4',
                String(snippet?.id).startsWith('temp-')
                  ? 'pointer-events-none opacity-50'
                  : 'bg-bg-primary',
              )} // Disable temporary rows from optimistic create snippet
            >
              <div className="w-full">
                <P
                  className={legacyTextStyles.body({
                    size: 'l',
                    weight: 'semibold',
                    className: 'mb-1',
                  })}
                >
                  {snippet.title}
                </P>
                <div
                  className={legacyTextStyles.body({
                    size: 's',
                    color: 'secondary',
                    className: 'max-w-3xl break-words',
                  })}
                >
                  <Markdown>{snippet.content ?? ''}</Markdown>
                </div>
              </div>
              <HStack justify="end">
                <NewButton
                  icon={{ name: 'Pencil' }}
                  onClick={() => editSnippet(String(snippet.id))}
                  type="outlineSubtle"
                />
                <NewButton
                  icon={{ name: 'Trash' }}
                  onClick={() => deleteSnippet(String(snippet.id))}
                  type="outlineSubtle"
                />
              </HStack>
            </HStack>
          ))
        )}
      </div>

      <SnippetModal
        snippet={
          editSnippetId
            ? snippets.find((s) => s.id === editSnippetId)
            : undefined
        }
        isOpen={isDialogOpen}
        onOpenChange={resetModal}
        onClose={resetModal}
        onSave={saveSnippet}
      />
    </div>
  );
};
